import { ReactElement, useRef, useEffect } from "react";

import { Box } from "@twilio-paste/core/box";
import { Chat, ChatLogger, useChatLogger } from "@twilio-paste/core/chat-log";
import { Bookend, ChatMessage, EventMessage, AgentTyping } from "./sub";

import { getAttribute, getMessageDirection, isEmptyObject } from "../../utils";

import { Message } from "@twilio/conversations";
import { AgentEvent } from "../../services/conversations/definitions/types";
import { ChatLogProps } from "./definitions";
import { styled } from "@twilio-paste/core/styling-library";
import { useScroll } from "@twiliointernal/e2-commons-shared-hooks";

const ChatLogContainer = styled(Box)<{ customStyles?: string }>`
  ${({ customStyles }) => (customStyles ? customStyles : "")}
`;

const ChatLog = ({
  agent,
  agentEvents,
  chatMessages,
  customer,
  dateCreated,
  participantFriendlyNames = {},
  isAgentTyping,
  appContainerId,
}: ChatLogProps): ReactElement => {
  const chatLogRef = useRef<HTMLDivElement | null>(null);
  const prevChatMessagesLengthRef = useRef<number>(chatMessages.length);
  const prevAgentEventsLengthRef = useRef<number>(0);
  const { chats, push: pushChat } = useChatLogger();

  const scrollContainerElement = useRef<HTMLElement>(
    document.querySelector(`[data-paste-element="${appContainerId}"]`)
  );
  const { updateScrollBottom } = useScroll(scrollContainerElement.current);

  // Compose message that will be pushed as item in <ChatLog />
  const composeMessage = (message: Message): Chat => {
    const messageDirection = getMessageDirection(customer, message);

    return {
      id: message.sid,
      content: (
        <ChatMessage
          message={message}
          messageDirection={messageDirection}
          participantFriendlyNames={participantFriendlyNames}
        />
      ),
      variant: messageDirection,
    };
  };

  // Compose event item (e.g. "Bob joined", "Bob left") that will be pushed as item in <ChatLog />
  const composeEvent = (event: AgentEvent): Chat => {
    const id = `${event.agent?.sid}_${event.action}`;
    const agentKey = event.agent.identity || "";

    const agentName = participantFriendlyNames[agentKey] || "Agent";

    return {
      id,
      content: <EventMessage action={event.action} name={agentName} />,
    };
  };

  // Push incoming chat as new chat messages are added
  useEffect(() => {
    if (!chats.length) {
      chatMessages.forEach((message) => {
        // TODO: Remove below when Live Chat in HCA fully launches
        const automatedAgentEventMessage = getAttribute({
          attributes: message.attributes,
          key: "automated",
        });

        // Currently, agent auto-generates and sends the message "bob has joined".
        // This is part of legacy feature that will be deprecated once Live Chat fully launches
        if (automatedAgentEventMessage) {
          return;
        }

        const composedMessage = composeMessage(message);
        pushChat(composedMessage);
      });
      return;
    }

    const isNewMessageAdded =
      prevChatMessagesLengthRef.current < chatMessages.length;

    if (isNewMessageAdded) {
      const latestChatMessage = chatMessages[chatMessages.length - 1];
      const composedMessage = composeMessage(latestChatMessage);
      pushChat(composedMessage);
    }

    prevChatMessagesLengthRef.current = chatMessages.length;
  }, [chatMessages]);

  // Push incoming events as new participant events are added
  useEffect(() => {
    const agentKey = agent?.identity || "";
    const isNewAgentEventAdded =
      prevAgentEventsLengthRef.current < agentEvents.length;
    const isFriendlyNamesEmpty = isEmptyObject(participantFriendlyNames);
    const isAgentFriendlyNameAvailable = participantFriendlyNames[agentKey];

    // Agent friendly name will always be availble to use unless there is an error in Conversation
    if (
      isNewAgentEventAdded &&
      !isFriendlyNamesEmpty &&
      isAgentFriendlyNameAvailable
    ) {
      const latestAgentEvent = agentEvents[agentEvents.length - 1];
      const composedEvent = composeEvent(latestAgentEvent);
      pushChat(composedEvent);
      prevAgentEventsLengthRef.current = agentEvents.length;
    }
  }, [agentEvents, participantFriendlyNames]);

  useEffect(() => {
    // Scroll to the bottom of ChatLog (fired when new messages are added) on next tick
    setTimeout(updateScrollBottom, 1);
  }, [chats, isAgentTyping]);

  // Make sure we use black color on text over the white background from the 'InlineCode' component from Paste
  // We need to force this color to override the white text color when the dark background is used on user messages
  const chatLogInnerStyles = `
  code[data-paste-element=INLINE_CODE] {
    color: rgb(18, 28, 45);
  }
  `;

  return (
    <ChatLogContainer
      backgroundColor="colorBackgroundBody"
      overflow="auto"
      overflowX="hidden"
      position="relative"
      paddingTop={["space0", "space70"]}
      ref={chatLogRef}
      customStyles={chatLogInnerStyles}
    >
      <Bookend dateCreated={dateCreated} type="started" />
      <ChatLogger chats={chats} />
      {agent && (
        <AgentTyping
          agent={agent}
          isTyping={isAgentTyping}
          participantFriendlyNames={participantFriendlyNames}
        />
      )}
    </ChatLogContainer>
  );
};

export default ChatLog;
